import {
  Stack,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  IconButton,
  Typography,
  Divider,
  Drawer,
  useMediaQuery,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FactoryIcon from '@mui/icons-material/Factory';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TuneIcon from '@mui/icons-material/TuneRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountProject } from '../../../contexts/AccountProjectContext';
import { IfCanAccess } from '@react-admin/ra-rbac';
import Logo from '../../../components/Logo';
import { LogoCoinhouseMobile } from '../../../components/Logo/LogoCoinhouse';
// A context switcher in the header: a white pill holding an icon, a small caption and
// the current value; the whole pill opens the select.
const ContextSelect = ({
  icon,
  label,
  value,
  onChange,
  disabled = false,
  fullWidth = false,
  children
}: {
  icon: ReactNode;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        height: 48,
        pl: 0.75,
        pr: 1,
        width: fullWidth ? '100%' : 'auto',
        minWidth: 220,
        borderRadius: 9999,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        opacity: disabled ? 0.55 : 1,
        transition: 'border-color .15s',
        '&:hover': disabled ? undefined : { borderColor: theme.palette.text.primary },
        '&:focus-within': { borderColor: theme.palette.primary.main }
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 34,
          height: 34,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          '& .MuiSvgIcon-root': { fontSize: 18 }
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="div"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            lineHeight: 1.2
          }}
        >
          {label}
        </Typography>
        <Select
          variant="standard"
          disableUnderline
          value={value}
          onChange={(e) => onChange(e.target.value as string)}
          disabled={disabled}
          displayEmpty
          fullWidth
          inputProps={{ 'aria-label': label }}
          MenuProps={{ PaperProps: { sx: { mt: 1, minWidth: 240 } } }}
          sx={{
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.3,
            '& .MuiSelect-select': {
              py: 0,
              pr: '28px !important',
              backgroundColor: 'transparent !important'
            },
            '& .MuiSelect-icon': { top: 'calc(50% - 16px)', right: 0 },
            // The select's hit area covers the whole pill.
            '& .MuiSelect-select::after': {
              content: '""',
              position: 'absolute',
              inset: '-24px -8px -8px -60px'
            }
          }}
        >
          {children}
        </Select>
      </Box>
    </Box>
  );
};

const HeaderMenu = () => {
  const { t }: { t: any } = useTranslation();
  const {
    accountId,
    setAccountId,
    projectId,
    setProjectId,
    accounts,
    filteredProjects,
    loading,
  } = useAccountProject();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  if (loading) return <CircularProgress size={20} />;

  const Menus = ({ fullWidth = false, isMobile = false }) => (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={1.5}
      alignItems={isMobile ? 'stretch' : 'center'}
      sx={fullWidth ? { width: '100%' } : undefined}
    >
      <IfCanAccess resource="accountsMenu" action="list">
        <ContextSelect
          icon={<AccountBalanceIcon />}
          label={t("Account")}
          value={accountId || ''}
          onChange={(value) => setAccountId(value)}
          fullWidth={fullWidth}
        >
          {accounts.map((account) => (
            <MenuItem key={account.id} value={account.id}>
              {account.name}
            </MenuItem>
          ))}
        </ContextSelect>
      </IfCanAccess>

      <IfCanAccess resource="projectsMenu" action="list">
        <ContextSelect
          icon={<FactoryIcon />}
          label={t("Entity")}
          value={projectId ?? ''}
          onChange={(value) => setProjectId(value === '' ? null : value)}
          disabled={!accountId}
          fullWidth={fullWidth}
        >
          <MenuItem value="">{t("All entities")}</MenuItem>
          {filteredProjects.map((project) => (
            <MenuItem key={project.id} value={project.id}>
              {project.name}
            </MenuItem>
          ))}
        </ContextSelect>
      </IfCanAccess>
    </Stack>
  );

  return (
    <>
      {isMobile ? (
        <>
{/*           <Box display="flex" justifyContent="left" alignItems="left" >
            <LogoCoinhouseMobile color="black" />
          </Box> */}

          <Button
            size="small"
            startIcon={<TuneIcon />}
            onClick={handleDrawerToggle}
            sx={{
              ml: 1,
              borderRadius: 9999,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary
            }}
          >
            {t('Account & entity')}
          </Button>
          <Drawer
            anchor="top"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            PaperProps={{ sx: { p: 2, borderRadius: '0 0 20px 20px' } }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="left">
              <Typography variant="h6">{t('Account & entity')}</Typography>
              <IconButton onClick={handleDrawerToggle}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Menus fullWidth isMobile={isMobile} />
          </Drawer>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Menus />
        </Box>
      )}
    </>
  );
};

export default HeaderMenu;
